import { Mood, SecondaryMood } from './Mood';

export type GradeBand = 'PK-5' | '6-12';
export type CheckinClassification = 'not-concerning' | 'down' | 'high-concern';
export type UrgencyTier = 'emerging' | 'concerning' | 'urgent';

const DOWN_PK5 = new Set<Mood | SecondaryMood>([Mood.NERVOUS, Mood.SAD, Mood.ANGRY]);

// Curated per PRD; not derivable from Mood.ts's structure.
const HIGH_CONCERN_6_12 = new Set<Mood | SecondaryMood>([
  SecondaryMood.SAD.DEPRESSED,
  SecondaryMood.SAD.LONELY,
  SecondaryMood.SCARED.INTIMIDATED,
  SecondaryMood.SCARED.ANXIOUS,
  SecondaryMood.ANGRY.HURT,
  SecondaryMood.SURPRISED.OVERWHELMED,
]);

const DOWN_CATEGORIES_6_12 = [SecondaryMood.SAD, SecondaryMood.SCARED, SecondaryMood.ANGRY];
// 6-12 check-ins are expected to always be a specific SecondaryMood (e.g.
// SecondaryMood.SAD.DEPRESSED), never the bare Mood itself - but classifyCheckin's
// signature accepts either, so these are included defensively.
const DOWN_PRIMARY_MOODS_6_12 = [Mood.SAD, Mood.SCARED, Mood.ANGRY];

const DOWN_6_12 = new Set<Mood | SecondaryMood>([
  ...DOWN_PRIMARY_MOODS_6_12,
  ...DOWN_CATEGORIES_6_12.flatMap((category) => Object.values(category)).filter(
    (feeling) => !HIGH_CONCERN_6_12.has(feeling),
  ),
]);

export function gradeToGradeBand(grade: number): GradeBand {
  return grade <= 5 ? 'PK-5' : '6-12';
}

export function classifyCheckin(
  feeling: Mood | SecondaryMood,
  gradeBand: GradeBand,
): CheckinClassification {
  if (gradeBand === 'PK-5') {
    return DOWN_PK5.has(feeling) ? 'down' : 'not-concerning';
  }

  if (HIGH_CONCERN_6_12.has(feeling)) {
    return 'high-concern';
  }

  return DOWN_6_12.has(feeling) ? 'down' : 'not-concerning';
}

export interface UrgencyState {
  consecutiveDownStreak: number;
  highConcernCount: number;
  lastHighConcernAt?: number;
}

export function applyCheckinToUrgencyState(
  state: UrgencyState,
  classification: CheckinClassification,
  checkinAt: number,
): UrgencyState {
  const consecutiveDownStreak =
    classification === 'not-concerning' ? 0 : state.consecutiveDownStreak + 1;

  if (classification !== 'high-concern') {
    return { ...state, consecutiveDownStreak };
  }

  // A gap longer than the rolling window means the prior episode already cleared,
  // so this check-in starts a fresh count rather than adding to a stale one.
  const isFreshEpisode =
    state.lastHighConcernAt !== undefined && checkinAt - state.lastHighConcernAt > FOUR_WEEKS_MS;

  return {
    consecutiveDownStreak,
    highConcernCount: isFreshEpisode ? 1 : state.highConcernCount + 1,
    lastHighConcernAt: checkinAt,
  };
}

export function tierFromUrgencyState(state: UrgencyState): UrgencyTier | null {
  if (state.highConcernCount >= 2) {
    return 'urgent';
  }

  if (state.highConcernCount >= 1) {
    return 'concerning';
  }

  return state.consecutiveDownStreak >= 3 ? 'emerging' : null;
}

export const FOUR_WEEKS_MS = 4 * 7 * 24 * 60 * 60 * 1000;

// Concerning/Urgent are evaluated against a rolling window (PRD, Sep 4, 2026),
// not an all-time count - shared by the report and the weekly/daily email jobs.
export function countRecentHighConcernCheckins(
  checkins: Array<{ feeling: Mood | SecondaryMood; createdAt: number }>,
  gradeBand: GradeBand,
  now: number,
  windowMs: number = FOUR_WEEKS_MS,
): number {
  const windowStart = now - windowMs;
  return checkins.filter(
    (checkin) =>
      checkin.createdAt >= windowStart &&
      checkin.createdAt <= now &&
      classifyCheckin(checkin.feeling, gradeBand) === 'high-concern',
  ).length;
}
